import { IconValue } from '../../../components';
import {
  DEFAULT_FURNITURE_FILL_COLOR,
  DEFAULT_FURNITURE_TEXT_COLOR,
  DEFAULT_ICON_FILL_COLOR,
  DEFAULT_ICON_TEXT_COLOR
} from '../components/map/defines';
import { getIconDimensions } from './tacticIconDimensions';
import { Furniture } from '../furniture-list';
import {
  FurnitureShape,
  IconShape,
  ImageShape,
  LayerType,
  SVGShape
} from '../types';
import { generateShapeId } from './generateShapeId';

export const sampleSvgPath = `
  M 10,30
  A 20,20 0,0,1 50,30
  A 20,20 0,0,1 90,30
  Q 90,60 50,90
  Q 10,60 10,30 z
`;

export const defaultSvgProps = {
  fill: '#E8EAEC',
  stroke: '#E8EAEC',
  strokeWidth: 2
};

export interface CreateSvgShapeOptions {
  x: number;
  y: number;
  width?: number;
  height?: number;
  layerType?: LayerType;
}

export interface CreateImageShapeOptions {
  x: number;
  y: number;
  width?: number;
  height?: number;
  layerType?: LayerType;
}

export const createSvgShape = (
  options: CreateSvgShapeOptions,
  path?: string,
  content?: string
): SVGShape => {
  return {
    id: generateShapeId('svg'),
    type: 'svg' as const,
    x: options.x,
    y: options.y,
    width: options.width || 100,
    height: options.height || 100,
    color: defaultSvgProps.fill,
    path: path || sampleSvgPath,
    svgContent: content || undefined,
    stroke: defaultSvgProps.stroke,
    strokeWidth: defaultSvgProps.strokeWidth,
    scaleX: 1,
    scaleY: 1,
    layerType: options.layerType || 'shapes'
  };
};

export const createImageShape = (
  options: CreateImageShapeOptions,
  path?: string
): ImageShape => {
  const imageFileNAme = path?.split('/').pop();
  return {
    id: generateShapeId('image'),
    type: 'image' as const,
    x: options.x,
    y: options.y,
    width: options.width || 100,
    height: options.height || 100,
    path: path || '',
    layerType: options.layerType || 'shapes',
    layerLabel: imageFileNAme
  };
};

export interface CreateIconShapeOptions {
  x: number;
  y: number;
  width?: number;
  height?: number;
  layerType?: LayerType;
  text?: string;
  svgContent?: string;
}

export const createIconShape = (
  options: CreateIconShapeOptions,
  icon: IconValue,
  iconUrl?: string
): IconShape => {
  // Compute aspect-ratio-preserving dimensions for tactic icons
  const defaultDims =
    icon.set === 'tactic'
      ? getIconDimensions(icon.icon, 100)
      : { width: 100, height: 100 };

  return {
    id: generateShapeId('icon'),
    type: 'icon' as const,
    x: options.x,
    y: options.y,
    width: options.width || defaultDims.width,
    height: options.height || defaultDims.height,
    color: DEFAULT_ICON_FILL_COLOR,
    textColor: DEFAULT_ICON_TEXT_COLOR,
    textAlign: 'center',
    icon,
    iconUrl,
    svgContent: options.svgContent,
    text: options.text || '',
    scaleX: 1,
    scaleY: 1,
    layerType: options.layerType || 'shapes',
    isBold: true
  };
};

export interface CreateFurnitureShapeOptions {
  x: number;
  y: number;
  width?: number;
  height?: number;
  layerType?: LayerType;
}

export const createFurnitureShape = (
  options: CreateFurnitureShapeOptions,
  furniture: Furniture
): FurnitureShape => {
  return {
    id: generateShapeId('furniture'),
    type: 'furniture' as const,
    x: options.x,
    y: options.y,
    width: options.width || 100,
    height: options.height || 100,
    color: DEFAULT_FURNITURE_FILL_COLOR,
    textColor: DEFAULT_FURNITURE_TEXT_COLOR,
    textAlign: 'center',
    furniture,
    scaleX: 1,
    scaleY: 1,
    layerType: options.layerType || 'shapes',
    text: '',
    isBold: true
  };
};

export const loadSvgFile = async (
  url: string
): Promise<{
  blobUrl: string;
  height: number;
  width: number;
}> => {
  const response = await fetch(url);
  const svgText = await response.text();

  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
  const svgElement = svgDoc.documentElement;

  // Prefer the viewBox over explicit width/height attributes. Many SVGs
  // (especially the hardcoded Tactic icons) author width/height as a
  // display hint that doesn't match the actual content aspect — e.g.,
  // width="100" height="100" with viewBox="0 0 104 53". The viewBox is
  // what describes the content's intrinsic aspect ratio, which is what
  // we want when placing the shape on the map. Fall back to the attrs
  // only when no viewBox is present.
  let width = 0;
  let height = 0;
  const vb = svgElement.getAttribute('viewBox');
  if (vb) {
    const parts = vb.split(/[\s,]+/).map(Number);
    if (parts.length === 4 && !parts.some(Number.isNaN)) {
      width = parts[2];
      height = parts[3];
    }
  }
  if (!width || !height) {
    const wAttr = Number(svgElement.getAttribute('width') || 0);
    const hAttr = Number(svgElement.getAttribute('height') || 0);
    if (!width) width = wAttr;
    if (!height) height = hAttr;
  }

  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  return {
    blobUrl: URL.createObjectURL(blob),
    height,
    width
  };
};
